// Estimate Invoices — bills raised straight off a quotation with no GST.
//
// A quotation is billed as usual; the rep then loads it here and raises an estimate invoice, which
// carries the same lines and the header discount but no tax at all (taxTotal 0, total = taxable
// value, numbered from the Estimate Invoice series). Like a proforma, the bill can be raised as one
// full payment or split into milestone stages — except a stage is a share of the tax-free total, and
// the stages are the team's own billing terms: the document handed to the client stays the plain
// quotation lines. Payments are recorded on the invoice itself, and can be pointed at a stage.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCompanyProfile } from '@/features/recruitment/api';
import { useDocumentMasterData } from '@/shared/hooks/useDocumentMasterData';
import { toast } from 'react-hot-toast';
import { Calculator, CreditCard, Eye, FileText, Plus, Wallet, XCircle, ChevronDown, Trash2 } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { getApiErrorMessage } from '@/shared/utils/apiError';
import { formatINR } from '@/shared/utils/currency';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { Modal } from '@/shared/components/ui/Modal';
import { SearchableSelect } from '@/shared/components/ui/SearchableSelect';
import {
  SalesDocumentWorkspaceDrawer,
  type DocumentWorkspaceData,
} from '@/components/sales/SalesDocumentWorkspaceDrawer';
import { generateFullTaxInvoiceHtml } from '@/utils/sales/quotationTemplateTokens';
import { parseStagePercents, stageName } from '@/utils/sales/paymentTermStages';

// A stage as stored on the document by the API.
interface EstimateStage {
  _id?: string;
  milestoneNumber?: number;
  name: string;
  percentage: number;
  dueDate?: string | null;
  status?: string;
  amountDue?: number;
  amountReceived?: number;
  amountOutstanding?: number;
}

// A stage while it is being typed into the form.
interface StageDraft {
  key: string;
  name: string;
  percent: number;
  dueDate: string;
}

const uid = () => Math.random().toString(36).slice(2, 9);

const STAGE_PRESETS: { label: string; shares: number[] }[] = [
  { label: '50% - 50%', shares: [50, 50] },
  { label: '40% - 30% - 30%', shares: [40, 30, 30] },
  { label: '30% - 40% - 30%', shares: [30, 40, 30] },
  { label: '25% × 4', shares: [25, 25, 25, 25] },
];

const draftFromShares = (shares: number[]): StageDraft[] =>
  shares.map((percent, i) => ({ key: uid(), name: stageName(i, shares.length), percent, dueDate: '' }));

const round2 = (n: number) => Math.round(n * 100) / 100;

const stageOutstanding = (stage: EstimateStage) => round2(Math.max(0, (stage.amountDue || 0) - (stage.amountReceived || 0)));

const isoDay = (raw: any) => {
  if (!raw) return '';
  const d = new Date(raw);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

// A master-data term can say "pay in N days" per stage; the estimate invoice is issued today, so
// that is the only anchor there is.
const dayFromIssue = (days: number) => (days > 0 ? new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10) : '');

// The bill the stages are carved out of: the quoted lines with no tax, plus any extra charge.
const billValue = (quotation: any) => round2(Number(quotation?.subtotal || 0) + Number(quotation?.additionalCharges || 0));

// Stages the quotation was itself agreed in, if it lists any — that split is what gets billed.
const quotationStages = (quotation: any): StageDraft[] => {
  const rows =
    quotation?.milestones?.length
      ? quotation.milestones
      : quotation?.paymentSchedule?.milestones?.length
        ? quotation.paymentSchedule.milestones
        : quotation?.template_data?.milestones || [];
  if (!Array.isArray(rows) || rows.length < 2) return [];
  return rows.map((m: any, i: number) => ({
    key: uid(),
    name: String(m?.name || m?.label || stageName(i, rows.length)),
    percent: Number(m?.percentage ?? m?.percent ?? 0) || 0,
    dueDate: isoDay(m?.dueDate),
  }));
};

// A payment schedule can also be written only as terms text, e.g. "30-40-30".
const termStages = (termName: string, schedule?: any[]): StageDraft[] => {
  const rows = (Array.isArray(schedule) && schedule.length
    ? schedule
        .map((r: any) => ({ percent: Number(r?.percentage) || 0, days: Number(r?.days) || 0, description: String(r?.description || '') }))
        .filter((r) => r.percent > 0)
    : (parseStagePercents(termName) || []).map((p) => ({ percent: p, days: 0, description: '' }))
  ).filter(Boolean);
  if (!rows.length) return [];
  if (Math.abs(rows.reduce((s, r) => s + r.percent, 0) - 100) > 0.5) return [];
  if (rows.length === 1) return [];
  return rows.map((r, i) => ({ key: uid(), name: stageName(i, rows.length, r.description), percent: r.percent, dueDate: dayFromIssue(r.days) }));
};

interface EstimateInvoice {
  _id: string;
  number: string;
  clientName?: string;
  clientEmail?: string;
  clientPhone?: string;
  clientAddress?: string;
  clientGstin?: string;
  sourceQuotationNumber?: string;
  sourceQuotationId?: string;
  date?: string;
  createdAt?: string;
  status: string;
  paymentStatus?: 'unpaid' | 'partially_paid' | 'paid';
  subtotal?: number;
  taxTotal?: number;
  total?: number;
  amountReceived?: number;
  balanceDue?: number;
  paymentTerms?: string;
  paymentType?: 'full_payment' | 'milestone_based';
  milestones?: EstimateStage[];
  notes?: string;
  terms?: string;
  items?: any[];
  payments?: Array<{ amount: number; paymentMode: string; referenceNo?: string; paidAt?: string; note?: string; recordedByName?: string }>;
  preparedBy?: string;
  [key: string]: any;
}

const PAYMENT_MODES = [
  { value: 'bank_transfer', label: 'Bank Transfer (NEFT/RTGS/IMPS)' },
  { value: 'upi', label: 'UPI / GPay / PhonePe' },
  { value: 'cheque', label: 'Cheque / Demand Draft' },
  { value: 'cash', label: 'Cash Receipt' },
];

function estimateStatusBadge(inv: EstimateInvoice) {
  if (inv.status === 'cancelled') {
    return <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#FEE2E2] text-[#DC2626] border border-[#FECACA]">Cancelled</span>;
  }
  const state = inv.paymentStatus || 'unpaid';
  const styles = {
    unpaid: 'bg-[#F1F3F9] text-[#64748B] border border-[#E2E8F0]',
    partially_paid: 'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A]',
    paid: 'bg-[#DCFCE7] text-[#16A34A] border border-[#BBF7D0]',
  }[state];
  const label = { unpaid: 'Unpaid', partially_paid: 'Partially paid', paid: 'Paid' }[state];
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${styles}`}>{label}</span>;
}

const asArray = (payload: any) =>
  Array.isArray(payload) ? payload : Array.isArray(payload?.requests) ? payload.requests : Array.isArray(payload?.data) ? payload.data : [];

const shortDate = (raw: any) => {
  if (!raw) return '';
  const d = new Date(raw);
  return isNaN(d.getTime()) ? String(raw) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export function EstimateInvoiceSection() {
  const queryClient = useQueryClient();
  const { data: companyProfile } = useCompanyProfile();
  const { data: masterData } = useDocumentMasterData();

  const [createOpen, setCreateOpen] = useState(false);
  const [quotationId, setQuotationId] = useState('');
  const [note, setNote] = useState('');
  const [paymentType, setPaymentType] = useState<'full_payment' | 'milestone_based'>('full_payment');
  const [stages, setStages] = useState<StageDraft[]>([]);
  const [paymentTermName, setPaymentTermName] = useState('');
  const [preparedByFilter, setPreparedByFilter] = useState('');
  const [paymentDoc, setPaymentDoc] = useState<EstimateInvoice | null>(null);
  const [cancelDoc, setCancelDoc] = useState<EstimateInvoice | null>(null);
  const [drawerDoc, setDrawerDoc] = useState<DocumentWorkspaceData | null>(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', paymentMode: 'bank_transfer', referenceNo: '', paidAt: '', note: '', milestoneIndex: null as number | null });

  const resetForm = () => {
    setQuotationId('');
    setNote('');
    setPaymentType('full_payment');
    setStages([]);
    setPaymentTermName('');
  };

  const { data: invoices, isLoading } = useQuery({
    queryKey: ['estimate-invoices'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/estimate-invoices');
        return asArray(data) as EstimateInvoice[];
      } catch {
        return [] as EstimateInvoice[];
      }
    },
    refetchInterval: 15000,
  });

  // Only quotations that are not already billed as an estimate can be loaded.
  const { data: quotations, isLoading: quotationsLoading } = useQuery({
    queryKey: ['estimate-invoice-quotations'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/invoice-requests?requestType=quotation&limit=500');
        return asArray(data);
      } catch {
        return [];
      }
    },
    enabled: createOpen,
  });

  const estimateIds = useMemo(
    () => new Set((invoices || []).map((i) => String(i.sourceQuotationId || ''))),
    [invoices]
  );

  const quotationOptions = useMemo(
    () =>
      (quotations || [])
        .filter((q: any) => q?.id || q?._id)
        .filter((q: any) => q.status !== 'cancelled' && (q.items?.length || q.lines?.length))
        .filter((q: any) => !estimateIds.has(String(q.id || q._id)))
        .map((q: any) => ({
          value: String(q.id || q._id),
          label: `${q.number || q.documentNumber || 'Quotation'} — ${q.clientName || 'No client'}`,
          description: `${formatINR(q.total || q.subtotal || 0)} · ${shortDate(q.date || q.createdAt)}`,
        })),
    [quotations, estimateIds]
  );

  const selectedQuotation = useMemo(
    () => (quotations || []).find((q: any) => String(q.id || q._id) === quotationId),
    [quotations, quotationId]
  );

  const activePaymentTerms = useMemo(
    () => (masterData?.paymentTerms ?? []).filter((t: any) => t.isActive !== false),
    [masterData]
  );

  const billTotal = billValue(selectedQuotation);
  const stageSum = round2(stages.reduce((s, m) => s + (Number(m.percent) || 0), 0));
  const stageShare = (percent: number) => round2((billTotal * (Number(percent) || 0)) / 100);
  const stagesReady = paymentType === 'full_payment' || (stages.length >= 2 && Math.abs(stageSum - 100) <= 0.5);

  // Picking the quotation decides the starting point: a quotation already agreed in stages opens the
  // form in milestone mode with that split filled in.
  const pickQuotation = (id: string) => {
    setQuotationId(id);
    const quotation = (quotations || []).find((q: any) => String(q.id || q._id) === id);
    const fromQuotation = quotationStages(quotation);
    const fromTerms = termStages(
      quotation?.paymentTerms || '',
      activePaymentTerms.find((t: any) => t.name === quotation?.paymentTerms)?.schedule
    );
    const prefill = fromQuotation.length ? fromQuotation : fromTerms;
    setPaymentTermName(quotation?.paymentTerms || '');
    if (prefill.length >= 2) {
      setPaymentType('milestone_based');
      setStages(prefill);
    } else {
      setPaymentType('full_payment');
      setStages([]);
    }
  };

  // Choosing a payment term drives the stages, the same way it does on a proforma.
  const applyTerm = (name: string) => {
    setPaymentTermName(name);
    const rows = termStages(name, activePaymentTerms.find((t: any) => t.name === name)?.schedule);
    if (!rows.length) return;
    setPaymentType('milestone_based');
    setStages(rows);
  };

  const setStage = (key: string, patch: Partial<StageDraft>) =>
    setStages((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  const visibleInvoices = useMemo(() => {
    const list = invoices || [];
    return preparedByFilter ? list.filter((i) => (i.preparedBy || '') === preparedByFilter) : list;
  }, [invoices, preparedByFilter]);

  const preparedByOptions = useMemo(
    () => Array.from(new Set((invoices || []).map((i) => i.preparedBy || '').filter(Boolean))).sort() as string[],
    [invoices]
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['estimate-invoices'] });

  const createEstimate = useMutation({
    mutationFn: () =>
      api
        .post(
          '/estimate-invoices',
          {
            quotationId,
            note,
            paymentType,
            paymentTerms: paymentTermName || undefined,
            milestones:
              paymentType === 'milestone_based'
                ? stages.map((s) => ({ name: s.name, percentage: Number(s.percent) || 0, dueDate: s.dueDate || undefined }))
                : undefined,
          },
          { skipErrorToast: true }
        )
        .then((r) => r.data?.data ?? r.data),
    onSuccess: (doc: any) => {
      toast.success(`Estimate invoice ${doc?.number || ''} created without GST.`);
      setCreateOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['estimate-invoice-quotations'] });
      invalidate();
    },
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'create the estimate invoice')),
  });

  const recordPayment = useMutation({
    mutationFn: () =>
      api
        .post(`/estimate-invoices/${paymentDoc!._id}/record-payment`, {
          amount: Number(paymentForm.amount),
          paymentMode: paymentForm.paymentMode,
          referenceNo: paymentForm.referenceNo,
          note: paymentForm.note,
          paidAt: paymentForm.paidAt || undefined,
          milestoneIndex: paymentForm.milestoneIndex ?? undefined,
        }, { skipErrorToast: true })
        .then((r) => r.data?.data ?? r.data),
    onSuccess: (doc: any) => {
      toast.success(`₹${formatINR(doc?.amountReceived || 0)} recorded on ${doc?.number || 'the invoice'}.`);
      setPaymentDoc(null);
      invalidate();
    },
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'record the payment')),
  });

  // A staged invoice opens on the earliest stage still owed money, so the common case is one click.
  const openPayment = (inv: EstimateInvoice) => {
    const stages = inv.milestones || [];
    const idx = inv.paymentType === 'milestone_based' ? stages.findIndex((s) => stageOutstanding(s) > 0) : -1;
    setPaymentDoc(inv);
    setPaymentForm({
      amount: String(idx >= 0 ? stageOutstanding(stages[idx]) : inv.balanceDue ?? inv.total ?? ''),
      paymentMode: 'bank_transfer',
      referenceNo: '',
      paidAt: new Date().toISOString().slice(0, 10),
      note: '',
      milestoneIndex: idx >= 0 ? idx : null,
    });
  };

  const cancelEstimate = useMutation({
    mutationFn: () => api.delete(`/estimate-invoices/${cancelDoc!._id}`, { skipErrorToast: true }).then((r) => r.data?.data ?? r.data),
    onSuccess: () => {
      toast.success('Estimate invoice cancelled.');
      setCancelDoc(null);
      invalidate();
    },
    onError: (err: any) => toast.error(getApiErrorMessage(err, 'cancel the estimate invoice')),
  });

  const openPreview = (inv: EstimateInvoice) => {
    const co = (companyProfile || {}) as any;
    const reg = co?.registeredAddress;
    const companyAddress =
      inv.businessAddress ||
      (reg ? [reg.line1, reg.line2, reg.city, reg.state, reg.pincode].filter(Boolean).join(', ') : '');

    const items = (inv.items || []).map((it: any) => ({
      name: it.name || it.description || 'Deliverable / service',
      description: it.name || it.description || '',
      subDescription: it.notes || '',
      notes: it.notes || '',
      hsn: it.hsnSac || '998314',
      quantity: Number(it.quantity ?? 1),
      unit: it.unit || 'Nos',
      unitPrice: Number(it.rate ?? 0),
      discountPercent: Number(it.discountPercent ?? 0),
      // No tax is charged, so no GST rate reaches the template.
      gstRate: 0,
    }));

    const isMilestone = inv.paymentType === 'milestone_based' || (Array.isArray(inv.milestones) && inv.milestones.length > 1);
    const totalAmount = Number(inv.total || inv.subtotal || 0);
    const milestones = isMilestone && Array.isArray(inv.milestones) && inv.milestones.length > 0
      ? inv.milestones.map((m: any, idx: number) => {
          const pct = Number(m.percentage ?? m.percent ?? 0);
          const calcAmount = round2((totalAmount * pct) / 100);
          const amt = Number(m.amountDue || m.fixedAmount || m.milestoneTotal || m.netAmount || calcAmount);
          return {
            name: m.name || m.label || `Stage ${idx + 1}`,
            percent: pct,
            percentage: pct,
            netAmount: amt,
            taxAmount: 0,
            cgstAmount: 0,
            sgstAmount: 0,
            igstAmount: 0,
            amountDue: amt,
            due: m.dueDate ? shortDate(m.dueDate) : (m.due || 'Due on receipt'),
            status: m.status || 'pending',
            invoiceNumber: m.invoiceNumber || '',
            paidAt: m.paidAt || undefined,
            exact: true,
          };
        })
      : undefined;

    const html = generateFullTaxInvoiceHtml({
      documentNumber: inv.number,
      invoiceNumber: inv.number,
      date: shortDate(inv.date || inv.createdAt),
      dueDate: inv.dueDate ? shortDate(inv.dueDate) : undefined,
      customerName: inv.clientName || '',
      customerEmail: inv.clientEmail,
      customerContact: inv.clientPhone,
      customerAddress: inv.clientAddress,
      customerGstin: inv.clientGstin,
      items,
      subtotal: inv.subtotal || 0,
      taxTotal: 0,
      total: totalAmount,
      paymentType: isMilestone ? 'milestone_based' : 'full_payment',
      paymentTerms: inv.paymentTerms || '',
      milestones,
      notes: inv.notes || '',
      terms: inv.terms || '',
      gstEnabled: false,
      gstMode: 'none',
      documentTitle: 'Estimate Invoice',
      signatureUrl: inv.signatureUrl || co?.signatureUrl || '',
      companyLogo: inv.businessLogo || inv.companyLogo || co?.logo || '',
      companyState: reg?.state || '',
      companyName: inv.businessName || co?.legalName || co?.name || '',
      companyAddress,
      companyGstin: inv.businessGstin || co?.gstin || '',
      companyPan: inv.businessPan || co?.pan || '',
      companyPhone: inv.businessPhone || co?.phone || '',
      companyEmail: inv.businessEmail || co?.officialEmail || co?.email || '',
      companyWeb: inv.businessWeb || co?.website || '',
      bankAccName: inv.bankAccName || (masterData?.bankDetails ?? []).find((b: any) => b.isDefault && b.isActive)?.accountName || (masterData?.bankDetails ?? []).find((b: any) => b.isActive)?.accountName || co?.bankDetails?.accountName || '',
      bankAccNo: inv.bankAccNo || (masterData?.bankDetails ?? []).find((b: any) => b.isDefault && b.isActive)?.accountNumber || (masterData?.bankDetails ?? []).find((b: any) => b.isActive)?.accountNumber || co?.bankDetails?.accountNumber || '',
      bankName: inv.bankName || (masterData?.bankDetails ?? []).find((b: any) => b.isDefault && b.isActive)?.bankName || (masterData?.bankDetails ?? []).find((b: any) => b.isActive)?.bankName || co?.bankDetails?.bankName || '',
      bankBranch: inv.bankBranch || (masterData?.bankDetails ?? []).find((b: any) => b.isDefault && b.isActive)?.branch || (masterData?.bankDetails ?? []).find((b: any) => b.isActive)?.branch || co?.bankDetails?.branch || '',
      bankIfsc: inv.bankIfsc || (masterData?.bankDetails ?? []).find((b: any) => b.isDefault && b.isActive)?.ifsc || (masterData?.bankDetails ?? []).find((b: any) => b.isActive)?.ifsc || co?.bankDetails?.ifscCode || '',
      bankUpi: inv.bankUpi || (masterData?.bankDetails ?? []).find((b: any) => b.isDefault && b.isActive)?.upi || (masterData?.bankDetails ?? []).find((b: any) => b.isActive)?.upi || '',
      defaultDeclaration: (masterData?.declarations ?? []).find((d: any) => d.isDefault && d.isActive !== false)?.content,
      // The template's declared input type predates the tax-free path; the renderer reads these keys.
    } as any);

    setDrawerDoc({
      id: inv._id,
      number: inv.number,
      documentNumber: inv.number,
      clientName: inv.clientName,
      status: inv.status,
      requestType: 'invoice',
      htmlBody: html,
      currentHtmlBody: html,
      subtotal: inv.subtotal,
      taxTotal: 0,
      total: inv.total,
      notes: inv.notes,
      paymentTerms: inv.paymentTerms,
      items,
    });
  };

  return (
    <div className="rounded-2xl border border-[#E8E8F5] bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Calculator className="h-6 w-6 text-[#6825F5]" />
          <div>
            <h2 className="text-lg font-bold tracking-tight text-[#11183F]">
              Estimate Invoices ({visibleInvoices.length})
            </h2>
            <p className="text-xs text-[#747C9C]">Bills raised from a quotation with no GST charged</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative min-w-[180px]">
            <select
              value={preparedByFilter}
              onChange={(e) => setPreparedByFilter(e.target.value)}
              aria-label="Filter by prepared by"
              className="w-full pl-3 pr-8 py-1.5 text-xs font-medium bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg text-[#11183F] focus:outline-none focus:ring-2 focus:ring-[#6825F5] focus:border-transparent cursor-pointer appearance-none shadow-xs"
            >
              <option value="">All Prepared By</option>
              {preparedByOptions.map((name) => (
                <option key={name} value={name}>Prepared by {name}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-[#747C9C]">
              <ChevronDown className="h-3.5 w-3.5" />
            </div>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> New Estimate Invoice
          </Button>
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden border border-[#F1F3F9]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-[#F8F9FE] text-[#747C9C] font-bold text-[11px] tracking-wider uppercase border-b border-[#F1F3F9]">
              <tr>
                <th className="px-5 py-3.5">Estimate Invoice</th>
                <th className="px-4 py-3.5">Client</th>
                <th className="px-4 py-3.5">Source Quotation</th>
                <th className="px-4 py-3.5">Date</th>
                <th className="px-4 py-3.5 text-right">Value (no GST)</th>
                <th className="px-4 py-3.5 text-right">Received</th>
                <th className="px-4 py-3.5">Status</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F3F9]">
              {isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 flex justify-center">
                    <Spinner />
                  </td>
                </tr>
              )}
              {!isLoading && visibleInvoices.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10">
                    <EmptyState
                      icon={<Calculator className="h-7 w-7 text-[#6825F5]" />}
                      title={preparedByFilter ? `No estimate invoices prepared by ${preparedByFilter}.` : 'No estimate invoices yet.'}
                      description={
                        preparedByFilter
                          ? 'Clear the Prepared By filter, or raise a new estimate invoice.'
                          : 'Load a quotation here and bill it without GST.'
                      }
                    />
                  </td>
                </tr>
              )}
              {!isLoading &&
                visibleInvoices.map((inv) => (
                  <tr key={inv._id} className="hover:bg-[#F8F9FE]/60 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold bg-[#EDE9FE] text-[#6825F5]">
                          <FileText className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-sm font-semibold text-[#11183F] leading-tight font-mono">{inv.number}</span>
                          {inv.paymentType === 'milestone_based' && (
                            <span className="block text-[11px] text-[#747C9C]">{(inv.milestones || []).length} stages · no GST</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm">
                      <div className="font-medium text-[#11183F]">{inv.clientName || 'No client'}</div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-xs text-[#6825F5]">{inv.sourceQuotationNumber || '—'}</span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-[#475569]">{shortDate(inv.date || inv.createdAt)}</td>
                    <td className="px-4 py-3.5 text-right font-mono font-semibold text-[#11183F]">{formatINR(inv.total || 0)}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-[#475569]">
                      {formatINR(inv.amountReceived || 0)}
                      {(inv.balanceDue || 0) > 0 && (
                        <span className="block text-[11px] text-[#D97706]">{formatINR(inv.balanceDue || 0)} due</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">{estimateStatusBadge(inv)}</td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => openPreview(inv)}
                          className="p-1.5 text-[#747C9C] hover:text-[#6825F5] hover:bg-[#EDE9FE] rounded-lg transition-colors cursor-pointer"
                          title="Preview / Print"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {inv.status !== 'cancelled' && (inv.balanceDue || 0) > 0 && (
                          <button
                            type="button"
                            onClick={() => openPayment(inv)}
                            className="p-1.5 text-[#747C9C] hover:text-[#16A34A] hover:bg-[#DCFCE7] rounded-lg transition-colors cursor-pointer"
                            title="Record payment"
                          >
                            <Wallet className="h-4 w-4" />
                          </button>
                        )}
                        {inv.status !== 'cancelled' && (inv.amountReceived || 0) === 0 && (
                          <button
                            type="button"
                            onClick={() => setCancelDoc(inv)}
                            className="p-1.5 text-[#747C9C] hover:text-[#DC2626] hover:bg-[#FEE2E2] rounded-lg transition-colors cursor-pointer"
                            title="Cancel invoice"
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Raise an estimate invoice from a quotation */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Estimate Invoice"
        size="lg"
        className="max-w-3xl min-h-[480px]"
        footer={
          <>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => createEstimate.mutate()} disabled={!quotationId || !stagesReady || createEstimate.isPending}>
              {createEstimate.isPending ? 'Creating…' : 'Generate Estimate Invoice'}
            </Button>
          </>
        }
      >
        <div className="space-y-4 py-1 flex-1">
          <p className="text-sm text-[#475569]">
            The quotation is billed as it stands, with every line shown at its taxable value and no GST added.
            The total payable is the taxable value.
          </p>
          <div>
            <label className="block text-xs font-semibold text-[#11183F] mb-1.5">Load a quotation</label>
            {quotationsLoading ? (
              <Spinner />
            ) : (
              <SearchableSelect
                options={quotationOptions}
                value={quotationId}
                onChange={pickQuotation}
                placeholder="Select a quotation to bill without tax"
                searchPlaceholder="Search by number or client…"
              />
            )}
            {quotationOptions.length === 0 && !quotationsLoading && (
              <p className="text-xs text-[#747C9C] mt-1.5">
                No quotation is available — every quotation is either already billed as an estimate or has no line items.
              </p>
            )}
          </div>

          {selectedQuotation && (
            <div className="rounded-xl border border-[#E8E8F5] bg-[#F8F9FE] p-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-[#11183F]">{selectedQuotation.clientName || 'No client'}</span>
                <span className="font-mono text-xs text-[#6825F5]">{selectedQuotation.number}</span>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-y-1.5 text-xs">
                <dt className="text-[#747C9C]">Lines</dt>
                <dd className="text-right text-[#11183F] font-medium">{(selectedQuotation.items || selectedQuotation.lines || []).length}</dd>
                <dt className="text-[#747C9C]">Taxable value</dt>
                <dd className="text-right text-[#11183F] font-mono">{formatINR(selectedQuotation.subtotal || 0)}</dd>
                {Number(selectedQuotation.additionalCharges || 0) > 0 && (
                  <>
                    <dt className="text-[#747C9C]">{selectedQuotation.additionalChargesLabel || 'Other charges'}</dt>
                    <dd className="text-right text-[#11183F] font-mono">{formatINR(selectedQuotation.additionalCharges || 0)}</dd>
                  </>
                )}
                <dt className="text-[#747C9C]">GST on the quotation</dt>
                <dd className="text-right text-[#16A34A] font-mono line-through decoration-[#16A34A]/40">
                  {formatINR(selectedQuotation.taxTotal || 0)}
                </dd>
                <dt className="text-[#747C9C] font-semibold">Estimate invoice total (no GST)</dt>
                <dd className="text-right text-[#6825F5] font-mono font-bold">{formatINR(billTotal)}</dd>
              </dl>
            </div>
          )}

          {/* Payment method: one tax-free bill, or the same total split into milestone stages */}
          {selectedQuotation && (
            <div className="rounded-xl border border-[#E8E8F5] p-4 space-y-3.5">
              <div className="flex items-start gap-2">
                <div className="p-1.5 rounded-lg bg-[#EDE9FE] text-[#6825F5]">
                  <CreditCard className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-[#11183F]">Payment Method &amp; Schedule</h3>
                  <p className="text-[11px] text-[#747C9C]">
                    {paymentType === 'milestone_based'
                      ? 'Every stage is billed on this one estimate invoice, and none of them carries GST'
                      : 'Single 100% payment for the whole bill'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label
                  className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                    paymentType === 'full_payment'
                      ? 'border-[#6825F5] bg-[#F8F9FE] ring-1 ring-[#6825F5]'
                      : 'border-[#E8E8F5] hover:border-[#D8D8EE]'
                  }`}
                >
                  <input
                    type="radio"
                    name="estimatePaymentType"
                    value="full_payment"
                    checked={paymentType === 'full_payment'}
                    onChange={() => {
                      setPaymentType('full_payment');
                      setStages([]);
                    }}
                    className="mt-0.5 h-4 w-4 text-[#6825F5] focus:ring-[#6825F5]"
                  />
                  <div>
                    <div className="text-sm font-semibold text-[#11183F]">Full Payment (100%)</div>
                    <div className="text-xs text-[#747C9C] mt-0.5">One payment of {formatINR(billTotal)}</div>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                    paymentType === 'milestone_based'
                      ? 'border-[#6825F5] bg-[#F8F9FE] ring-1 ring-[#6825F5]'
                      : 'border-[#E8E8F5] hover:border-[#D8D8EE]'
                  }`}
                >
                  <input
                    type="radio"
                    name="estimatePaymentType"
                    value="milestone_based"
                    checked={paymentType === 'milestone_based'}
                    onChange={() => {
                      setPaymentType('milestone_based');
                      if (stages.length === 0) setStages(draftFromShares([50, 50]));
                    }}
                    className="mt-0.5 h-4 w-4 text-[#6825F5] focus:ring-[#6825F5]"
                  />
                  <div>
                    <div className="text-sm font-semibold text-[#11183F]">Milestone-Based Payment</div>
                    <div className="text-xs text-[#747C9C] mt-0.5">Break the bill into stage shares, still with no GST</div>
                  </div>
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="estimate-payment-term" className="block text-xs font-semibold text-[#11183F] mb-1.5">Payment term</label>
                  <select
                    id="estimate-payment-term"
                    value={activePaymentTerms.some((t: any) => t.name === paymentTermName) ? paymentTermName : ''}
                    onChange={(e) => applyTerm(e.target.value)}
                    className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#6825F5]/40"
                  >
                    <option value="">— Select a payment term —</option>
                    {activePaymentTerms.map((t: any) => (
                      <option key={t._id} value={t.name}>{t.name}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-[#747C9C] mt-1">A term that carries a schedule fills the stages below.</p>
                </div>
                <div>
                  <label htmlFor="estimate-terms-text" className="block text-xs font-semibold text-[#11183F] mb-1.5">Terms text on the invoice</label>
                  <input
                    id="estimate-terms-text"
                    type="text"
                    value={paymentTermName}
                    onChange={(e) => setPaymentTermName(e.target.value)}
                    onBlur={() => paymentType === 'milestone_based' && applyTerm(paymentTermName)}
                    placeholder="e.g. 30-40-30 across advance, mid and final"
                    className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6825F5]/40"
                  />
                  <p className="text-[11px] text-[#747C9C] mt-1">Leave blank to bill it as due on receipt.</p>
                </div>
              </div>

              {paymentType === 'milestone_based' && (
                <div className="space-y-3 pt-0.5">
                  <div className="flex flex-wrap items-center justify-between gap-2.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-medium text-[#747C9C] mr-1">Presets:</span>
                      {STAGE_PRESETS.map((preset) => (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => setStages(draftFromShares(preset.shares))}
                          className="text-xs px-2.5 py-1 rounded-md bg-[#F8F9FE] border border-[#E8E8F5] text-[#475569] hover:bg-[#EDE9FE] transition-colors"
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const remaining = Math.max(0, 100 - stageSum);
                        setStages((prev) => [
                          ...prev,
                          { key: uid(), name: stageName(prev.length, prev.length + 1), percent: remaining > 0 ? remaining : 10, dueDate: '' },
                        ]);
                      }}
                      className="flex items-center gap-1.5 text-xs font-semibold text-[#6825F5] bg-[#EDE9FE] border border-[#DDD6FE] rounded-lg px-3 py-1.5 hover:bg-[#DDD6FE] transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Stage
                    </button>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-[#E8E8F5]">
                    <table className="w-full text-xs min-w-[620px]">
                      <thead className="bg-[#F8F9FE] text-[#747C9C] font-semibold">
                        <tr>
                          <th className="px-3 py-2.5 text-center w-10">#</th>
                          <th className="px-3 py-2.5 text-left">Stage / Milestone Name</th>
                          <th className="px-3 py-2.5 text-center w-24">% Share</th>
                          <th className="px-3 py-2.5 text-right w-32">Amount (no GST)</th>
                          <th className="px-3 py-2.5 text-left w-36">Due Date</th>
                          <th className="px-3 py-2.5 text-center w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F1F3F9]">
                        {stages.map((s, idx) => (
                          <tr key={s.key}>
                            <td className="px-3 py-2 text-center font-bold text-[#A5AAC7]">{idx + 1}</td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={s.name}
                                onChange={(e) => setStage(s.key, { name: e.target.value })}
                                placeholder={`Stage ${idx + 1} name`}
                                className="w-full rounded-md border border-[#E2E8F0] px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#6825F5]"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="relative flex items-center">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.5"
                                  value={s.percent === 0 ? '' : s.percent}
                                  onChange={(e) => setStage(s.key, { percent: e.target.value === '' ? 0 : Number(e.target.value) || 0 })}
                                  placeholder="0"
                                  className="w-full rounded-md border border-[#E2E8F0] px-2.5 py-1.5 pr-6 text-xs text-center font-medium focus:outline-none focus:ring-1 focus:ring-[#6825F5]"
                                />
                                <span className="absolute right-2.5 text-[#A5AAC7] pointer-events-none text-xs">%</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right font-mono font-semibold text-[#11183F]">{formatINR(stageShare(s.percent))}</td>
                            <td className="px-3 py-2">
                              <input
                                type="date"
                                value={s.dueDate}
                                onChange={(e) => setStage(s.key, { dueDate: e.target.value })}
                                className="w-full rounded-md border border-[#E2E8F0] px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#6825F5]"
                              />
                            </td>
                            <td className="px-3 py-2 text-center">
                              {stages.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => setStages((prev) => prev.filter((x) => x.key !== s.key))}
                                  className="p-1.5 rounded-md text-[#DC2626] hover:bg-[#FEE2E2] transition-colors"
                                  title="Delete stage"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-[#F8F9FE] border-t border-[#E8E8F5] font-bold">
                        <tr>
                          <td colSpan={2} className="px-3.5 py-2.5 text-left text-[#475569]">
                            Total Milestone Distribution
                          </td>
                          <td className="px-3.5 py-2.5 text-center">
                            <span
                              className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                                stageSum === 100
                                  ? 'bg-[#DCFCE7] text-[#16A34A]'
                                  : 'bg-[#FEE2E2] text-[#DC2626]'
                              }`}
                            >
                              {stageSum}% {stageSum === 100 ? '✓' : `(needs ${round2(100 - stageSum)}%)`}
                            </span>
                          </td>
                          <td className="px-3.5 py-2.5 text-right font-mono text-[#6825F5]">{formatINR(stageShare(stageSum))}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {stageSum !== 100 && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-[#FFFBEB] border border-[#FDE68A] text-xs text-[#D97706]">
                      <span className="font-bold">Note:</span>
                      <span>
                        The stage shares add up to <b>{stageSum}%</b>. They must total <b>100%</b> of {formatINR(billTotal)} before the
                        invoice can be generated.
                      </span>
                    </div>
                  )}

                  <p className="text-[11px] text-[#747C9C] italic">
                    These stages are how your team bills and chases the money. The invoice issued to the client stays the quoted lines in
                    full, with no GST and no stage breakdown on it.
                  </p>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-[#11183F] mb-1.5">Note for this invoice (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6825F5]/40"
              placeholder="e.g. Tax not applicable — billed as an estimate"
            />
          </div>
        </div>
      </Modal>

      {/* Record a payment against the invoice */}
      <Modal
        open={!!paymentDoc}
        onClose={() => setPaymentDoc(null)}
        title={`Record payment — ${paymentDoc?.number || ''}`}
        footer={
          <>
            <Button variant="outline" onClick={() => setPaymentDoc(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => recordPayment.mutate()}
              disabled={!Number(paymentForm.amount) || recordPayment.isPending}
            >
              {recordPayment.isPending ? 'Saving…' : 'Record Payment'}
            </Button>
          </>
        }
      >
        <div className="space-y-4 py-1">
          <div className="flex items-center justify-between rounded-xl border border-[#E8E8F5] bg-[#F8F9FE] px-4 py-3 text-sm">
            <span className="text-[#747C9C] font-medium">Outstanding</span>
            <span className="font-mono font-bold text-[#11183F]">{formatINR(paymentDoc?.balanceDue || 0)}</span>
          </div>

          {(paymentDoc?.milestones || []).length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-[#11183F] mb-1.5">Settle against a stage</label>
              <div className="space-y-1.5">
                {paymentDoc!.milestones!.map((s, idx) => {
                  const due = stageOutstanding(s);
                  const active = paymentForm.milestoneIndex === idx;
                  return (
                    <button
                      key={s._id || idx}
                      type="button"
                      disabled={due <= 0}
                      onClick={() => setPaymentForm((f) => ({ ...f, milestoneIndex: idx, amount: String(due) }))}
                      className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-xs transition-colors ${
                        active
                          ? 'border-[#6825F5] bg-[#F8F9FE] font-semibold text-[#11183F]'
                          : 'border-[#E8E8F5] text-[#475569] hover:border-[#D8D8EE]'
                      } ${due <= 0 ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      <span className="truncate">{idx + 1}. {s.name}</span>
                      <span className="font-mono shrink-0">{formatINR(s.amountDue || 0)}</span>
                      <span className={`font-mono shrink-0 ${due > 0 ? 'text-[#D97706]' : 'text-[#16A34A]'}`}>
                        {due > 0 ? `${formatINR(due)} due` : 'Cleared'}
                      </span>
                    </button>
                  );
                })}
                {paymentForm.milestoneIndex !== null && (
                  <button
                    type="button"
                    onClick={() =>
                      setPaymentForm((f) => ({ ...f, milestoneIndex: null, amount: String(paymentDoc?.balanceDue ?? '') }))
                    }
                    className="text-[11px] font-semibold text-[#6825F5] hover:underline"
                  >
                    Take it against the invoice as a whole instead
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-semibold text-xs text-[#11183F] mb-1.5">Amount received</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm((f) => ({ ...f, amount: e.target.value }))}
                className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#6825F5]/40"
              />
            </div>
            <div>
              <label className="block font-semibold text-xs text-[#11183F] mb-1.5">Payment method</label>
              <select
                value={paymentForm.paymentMode}
                onChange={(e) => setPaymentForm((f) => ({ ...f, paymentMode: e.target.value }))}
                className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#6825F5]/40"
              >
                {PAYMENT_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-semibold text-xs text-[#11183F] mb-1.5">Ref / UTR / Cheque No.</label>
              <input
                type="text"
                value={paymentForm.referenceNo}
                onChange={(e) => setPaymentForm((f) => ({ ...f, referenceNo: e.target.value }))}
                className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6825F5]/40"
              />
            </div>
            <div>
              <label className="block font-semibold text-xs text-[#11183F] mb-1.5">Date received</label>
              <input
                type="date"
                value={paymentForm.paidAt}
                onChange={(e) => setPaymentForm((f) => ({ ...f, paidAt: e.target.value }))}
                className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6825F5]/40"
              />
            </div>
          </div>
          <div>
            <label className="block font-semibold text-xs text-[#11183F] mb-1.5">Note (optional)</label>
            <input
              type="text"
              value={paymentForm.note}
              onChange={(e) => setPaymentForm((f) => ({ ...f, note: e.target.value }))}
              className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6825F5]/40"
            />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!cancelDoc}
        onCancel={() => setCancelDoc(null)}
        onConfirm={() => cancelEstimate.mutate()}
        title="Cancel this estimate invoice?"
        message={`${cancelDoc?.number || 'This invoice'} will be marked cancelled. It can be raised again from the quotation afterwards.`}
        confirmLabel="Cancel invoice"
        danger
        loading={cancelEstimate.isPending}
      />

      <SalesDocumentWorkspaceDrawer
        isOpen={!!drawerDoc}
        onClose={() => setDrawerDoc(null)}
        document={drawerDoc}
        readOnly
      />
    </div>
  );
}
